// Isometric Hold Timer — pure timeline + clock maths (no React, no DOM).
// The UI builds a timeline from the config, then asks `stateAt(timeline, ms)`
// on every animation frame; pausing just stops advancing `ms`.
// Unit tests: tests/holdTimer.test.ts.

export type ArmMode = 'right' | 'left' | 'alternate';

export interface TimerConfig {
  prepSec: number;
  holdSec: number;
  /** Rest between holds (in alternate mode: the switch time between arms). */
  restSec: number;
  /** Holds per set (per arm). */
  rounds: number;
  sets: number;
  /** Longer rest between sets. */
  setRestSec: number;
  arm: ArmMode;
}

export const LIMITS = {
  prepSec: [0, 30],
  holdSec: [3, 120],
  restSec: [0, 300],
  rounds: [1, 20],
  sets: [1, 10],
  setRestSec: [0, 600],
} as const;

export const DEFAULT_CONFIG: TimerConfig = {
  prepSec: 10,
  holdSec: 15,
  restSec: 45,
  rounds: 4,
  sets: 1,
  setRestSec: 120,
  arm: 'alternate',
};

// Presets for common armwrestling statics, each tied to an existing exercise
// page. Doses are conservative starting points within the library's own range.
export const PRESETS: { id: string; exerciseId: string; slug: string; config: Partial<TimerConfig> }[] = [
  { id: 'pronation', exerciseId: 'staticPronationHold45', slug: 'static-pronation-hold-45', config: { holdSec: 15, restSec: 45, rounds: 4 } },
  { id: 'cup', exerciseId: 'twoByTwoHandleFingerHold', slug: 'two-by-two-handle-finger-hold', config: { holdSec: 15, restSec: 45, rounds: 4 } },
  { id: 'sidePressure', exerciseId: 'staticBarWristCurlsSidePressure', slug: 'static-bar-wrist-curls-side-pressure', config: { holdSec: 12, restSec: 60, rounds: 3 } },
  { id: 'supination', exerciseId: 'staticSupinationHold', slug: 'static-supination-hold', config: { holdSec: 15, restSec: 45, rounds: 4 } },
  { id: 'riser', exerciseId: 'staticRiserHold', slug: 'static-riser-hold', config: { holdSec: 15, restSec: 60, rounds: 3 } },
  { id: 'reverseWrist', exerciseId: 'staticReverseWristCurlHold', slug: 'static-reverse-wrist-curl-hold', config: { holdSec: 12, restSec: 45, rounds: 3 } },
];

export type PhaseKind = 'prep' | 'hold' | 'rest' | 'setRest';

export interface Phase {
  kind: PhaseKind;
  durationSec: number;
  set: number; // 1-based
  round: number; // 1-based
  arm: 'right' | 'left' | null;
  /** Start offset from the beginning of the session, seconds. */
  startSec: number;
}

const clamp = (n: number, [lo, hi]: readonly [number, number]) =>
  Math.min(hi, Math.max(lo, Math.round(Number.isFinite(n) ? n : lo)));

export function sanitize(c: Partial<TimerConfig>): TimerConfig {
  const m = { ...DEFAULT_CONFIG, ...c };
  return {
    prepSec: clamp(m.prepSec, LIMITS.prepSec),
    holdSec: clamp(m.holdSec, LIMITS.holdSec),
    restSec: clamp(m.restSec, LIMITS.restSec),
    rounds: clamp(m.rounds, LIMITS.rounds),
    sets: clamp(m.sets, LIMITS.sets),
    setRestSec: clamp(m.setRestSec, LIMITS.setRestSec),
    arm: (['right', 'left', 'alternate'] as const).includes(m.arm) ? m.arm : DEFAULT_CONFIG.arm,
  };
}

/**
 * Session timeline. Alternate mode does right then left inside each round, with
 * `restSec` after every hold, so each arm rests while the other works. The last
 * hold of the session has no trailing rest; the last hold of a set is followed
 * by the set rest instead of the normal rest.
 */
export function buildTimeline(input: Partial<TimerConfig>): Phase[] {
  const c = sanitize(input);
  const arms: ('right' | 'left')[] = c.arm === 'alternate' ? ['right', 'left'] : [c.arm];
  const out: Phase[] = [];
  let t = 0;
  const push = (p: Omit<Phase, 'startSec'>) => {
    if (p.durationSec <= 0) return;
    out.push({ ...p, startSec: t });
    t += p.durationSec;
  };
  push({ kind: 'prep', durationSec: c.prepSec, set: 1, round: 1, arm: arms[0] });
  for (let s = 1; s <= c.sets; s++) {
    for (let r = 1; r <= c.rounds; r++) {
      arms.forEach((arm, ai) => {
        push({ kind: 'hold', durationSec: c.holdSec, set: s, round: r, arm });
        const lastOfRound = ai === arms.length - 1;
        const lastOfSet = lastOfRound && r === c.rounds;
        const lastOfAll = lastOfSet && s === c.sets;
        if (lastOfAll) return;
        const next = lastOfRound ? arms[0] : arms[ai + 1];
        if (lastOfSet) push({ kind: 'setRest', durationSec: c.setRestSec, set: s, round: r, arm: next });
        else push({ kind: 'rest', durationSec: c.restSec, set: s, round: r, arm: next });
      });
    }
  }
  return out;
}

export const totalSec = (tl: Phase[]) => (tl.length ? tl[tl.length - 1].startSec + tl[tl.length - 1].durationSec : 0);

export interface ClockState {
  done: boolean;
  index: number; // -1 when done
  phase: Phase | null;
  next: Phase | null;
  /** Whole seconds left in the current phase (ceil), for display. */
  remainingSec: number;
  /** 0..1 progress through the current phase. */
  phaseProgress: number;
  elapsedSec: number;
  holdsCompleted: number;
  timeUnderTensionSec: number;
}

export function stateAt(tl: Phase[], elapsedMs: number): ClockState {
  const el = Math.max(0, elapsedMs) / 1000;
  const total = totalSec(tl);
  let holdsCompleted = 0;
  let tut = 0;
  for (const p of tl) {
    if (p.kind !== 'hold') continue;
    if (el >= p.startSec + p.durationSec) {
      holdsCompleted++;
      tut += p.durationSec;
    } else if (el > p.startSec) tut += el - p.startSec;
  }
  if (el >= total) {
    return { done: true, index: -1, phase: null, next: null, remainingSec: 0, phaseProgress: 1, elapsedSec: total, holdsCompleted, timeUnderTensionSec: Math.round(tut) };
  }
  // Binary search would be overkill: sessions are < 500 phases.
  let i = 0;
  while (i < tl.length - 1 && el >= tl[i].startSec + tl[i].durationSec) i++;
  const p = tl[i];
  const into = el - p.startSec;
  return {
    done: false,
    index: i,
    phase: p,
    next: tl[i + 1] || null,
    remainingSec: Math.max(1, Math.ceil(p.durationSec - into - 1e-9)),
    phaseProgress: Math.min(1, into / p.durationSec),
    elapsedSec: el,
    holdsCompleted,
    timeUnderTensionSec: Math.floor(tut),
  };
}

/** Summary for the printable card (whole planned session). */
export function sessionSummary(input: Partial<TimerConfig>) {
  const c = sanitize(input);
  const tl = buildTimeline(c);
  const holds = tl.filter((p) => p.kind === 'hold');
  const perArm = (arm: 'right' | 'left') => holds.filter((h) => h.arm === arm).length;
  return {
    config: c,
    totalSec: totalSec(tl),
    holdsRight: perArm('right'),
    holdsLeft: perArm('left'),
    tutPerArmSec: {
      right: perArm('right') * c.holdSec,
      left: perArm('left') * c.holdSec,
    },
  };
}

export function formatClock(sec: number): string {
  const s = Math.max(0, Math.round(sec));
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
}

// Share URL: ?prep=10&hold=15&rest=45&rounds=4&sets=1&setrest=120&arm=alternate[&preset=pronation]
export function parseConfig(params: URLSearchParams): { config: TimerConfig; preset: string | null } {
  const num = (k: string) => (params.has(k) ? Number(params.get(k)) : undefined);
  const raw: Partial<TimerConfig> = {};
  const map: [keyof TimerConfig, string][] = [['prepSec', 'prep'], ['holdSec', 'hold'], ['restSec', 'rest'], ['rounds', 'rounds'], ['sets', 'sets'], ['setRestSec', 'setrest']];
  for (const [key, q] of map) {
    const v = num(q);
    if (v !== undefined && Number.isFinite(v)) (raw as Record<string, number>)[key] = v;
  }
  const arm = params.get('arm');
  if (arm === 'right' || arm === 'left' || arm === 'alternate') raw.arm = arm;
  const preset = params.get('preset');
  return { config: sanitize(raw), preset: PRESETS.some((p) => p.id === preset) ? preset : null };
}

export function serializeConfig(c: TimerConfig, preset: string | null = null): string {
  const p = new URLSearchParams({
    prep: String(c.prepSec),
    hold: String(c.holdSec),
    rest: String(c.restSec),
    rounds: String(c.rounds),
    sets: String(c.sets),
    setrest: String(c.setRestSec),
    arm: c.arm,
  });
  if (preset) p.set('preset', preset);
  return p.toString();
}
